import React from 'react';
import { X, RotateCcw, Save } from 'lucide-react';
import { useDeviceUxProfile } from '@/shared/device/useDeviceUxProfile';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { cn, type ThemeClasses } from '@/ui/theme/ThemeContext';
import { keyboardManager } from '@/shared/services/KeyboardEventManager';

/* ============================================================================
 * TYPE DEFINITIONS
 * ============================================================================ */

export interface ModalWrapperProps {
  isOpen: boolean;
  onClose: () => void;
  modalRef: React.RefObject<HTMLDivElement>;
  modalHeight: number | 'auto';
  modalWidth: number | 'auto';
  theme: ThemeClasses;
  children: React.ReactNode;
  focusOwnerId?: string;
}

export interface PanelHeaderProps {
  title: string;
  hasChanges: boolean;
  canUndo?: boolean;
  undoTitle?: string;
  onTitleChange: (value: string) => void;
  /** Commit the title at an explicit editing boundary, never per keystroke. */
  onTitleCommit?: () => void;
  /** Restore the pre-edit title without sending a write. */
  onTitleCancel?: () => void;
  onUndo: () => void;
  editable?: boolean;
  theme: ThemeClasses;
}

export interface PanelFooterProps {
  onSave: () => void;
  disabled?: boolean;
  disabledReason?: string;
  theme: ThemeClasses;
}

/* ============================================================================
 * MODAL WRAPPER COMPONENT
 * Provides the modal backdrop and container
 * ============================================================================ */

export const ModalWrapper: React.FC<ModalWrapperProps> = ({
  isOpen,
  onClose,
  modalRef,
  modalHeight,
  modalWidth,
  theme,
  children,
  focusOwnerId = 'task-details-panel'
}) => {
  const deviceUx = useDeviceUxProfile();
  const { t } = useSettings();
  const isIphoneTouch = deviceUx.mode === 'iphone-touch';
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const openerRef = React.useRef<HTMLElement | null>(null);
  const openedRef = React.useRef(false);
  if (isOpen && !openedRef.current && typeof document !== 'undefined') {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  openedRef.current = isOpen;
  React.useLayoutEffect(() => { if (modalRef.current) modalRef.current.inert = !isOpen; }, [isOpen, modalRef]);
  React.useEffect(() => {
    const overlay = overlayRef.current, view = window.visualViewport;
    if (!isOpen || !overlay || !view) return undefined;
    const update = () => {
      const focused = overlay.contains(document.activeElement) && document.activeElement?.matches('input,textarea,[contenteditable="true"]');
      const keyboard = (focused || overlay.dataset.keyboardViewport === 'true') && view.scale === 1 && window.innerHeight - view.height > 40;
      overlay.dataset.keyboardViewport = String(Boolean(keyboard));
      overlay.style.setProperty('--pm-task-visible-top', `${view.offsetTop}px`);
      overlay.style.setProperty('--pm-task-visible-height', `${view.height}px`);
    };
    view.addEventListener('resize', update);view.addEventListener('scroll', update);
    overlay.addEventListener('focusin', update);overlay.addEventListener('focusout', update);
    update();
    return () => { view.removeEventListener('resize', update);view.removeEventListener('scroll', update);overlay.removeEventListener('focusin', update);overlay.removeEventListener('focusout', update); };
  }, [isOpen]);
  React.useEffect(() => {
    const overlay = overlayRef.current;
    if (!isOpen || !overlay) return undefined;
    closeRef.current?.focus({ preventScroll: true });
    const controls = () => Array.from(overlay.querySelectorAll<HTMLElement>('*')).filter(element => element.matches(
      'button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],[tabindex="0"]',
    )).filter(element => !element.closest('[hidden],[inert]') && window.getComputedStyle(element).display !== 'none' && window.getComputedStyle(element).visibility !== 'hidden');
    const tab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.defaultPrevented || !keyboardManager.isModalOnTop(focusOwnerId)) return;
      const items = controls(), first = items[0], last = items[items.length - 1], active = document.activeElement;
      if (!first) return;
      if (!overlay.contains(active) || (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
        event.preventDefault();(event.shiftKey ? last : first)?.focus();
      }
    };
    document.addEventListener('keydown', tab);
    return () => {
      document.removeEventListener('keydown', tab);
      if (openerRef.current?.isConnected && !openerRef.current.matches(':disabled')) openerRef.current.focus({ preventScroll: true });
    };
  }, [isOpen, focusOwnerId]);

  return (
    <div 
      ref={overlayRef}
      data-testid="task-details-modal-overlay"
      role="dialog"
      aria-modal={isOpen || undefined}
      aria-label={t('Task details')}
      className={cn(
        "pm-task-details-overlay fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000] flex justify-center overflow-y-auto transition-opacity duration-200",
        isIphoneTouch
          ? "items-stretch p-0"
          : "items-start sm:items-center p-4",
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      )} 
      onClick={onClose}
    >
      <div className={cn("relative w-full", isIphoneTouch ? "h-full max-w-none" : "my-auto max-w-2xl")}>
        <div 
          ref={modalRef}
          data-testid="task-details-modal"
          className={cn(
            "pm-task-details-modal relative w-full shadow-2xl transform overflow-hidden flex flex-col",
            isIphoneTouch
              ? "h-[100dvh] max-h-none rounded-none"
              : "max-h-[calc(100vh-2rem)] rounded-2xl",
            "transition-[transform,opacity] duration-200",
            "transition-[height,width] duration-[200ms] ease-in-out",
            theme.bgPrimary,
            theme.textPrimary,
            isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          )}
          style={{
            height: isIphoneTouch ? '100dvh' : modalHeight,
            width: isIphoneTouch ? '100vw' : modalWidth,
            minWidth: isIphoneTouch ? undefined : 'min(42rem, calc(100vw - 2rem))',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
          {isIphoneTouch ? (
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              id="task-details-close-button"
              aria-label={t('Close task details')}
              className={cn(
                'absolute right-3 top-[calc(0.75rem+env(safe-area-inset-top))] z-[10001] p-2 rounded-full shadow-lg transition-all',
                theme.bgSecondary,
                theme.textPrimary,
                theme.hover,
                isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
              )}
            >
              <X size={20} />
            </button>
          ) : null}
        </div>
        {!isIphoneTouch ? (
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            id="task-details-close-button"
            aria-label={t('Close task details')}
            className={cn(
              'absolute -top-3 -right-3 z-[10001] p-2 rounded-full shadow-lg transition-all',
              theme.bgSecondary,
              theme.textPrimary,
              theme.hover,
              isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
            )}
          >
            <X size={20} />
          </button>
        ) : null}
      </div>
    </div>
  );
};

/* ============================================================================
 * PANEL HEADER COMPONENT
 * Header with title input and undo button
 * ============================================================================ */

export const PanelHeader: React.FC<PanelHeaderProps> = ({
  title,
  hasChanges,
  canUndo = hasChanges,
  undoTitle,
  onTitleChange,
  onTitleCommit,
  onTitleCancel,
  onUndo,
  editable = true,
  theme
}) => {
  const { t } = useSettings();
  const skipNextBlurCommit = React.useRef(false);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      // Blurring after Escape must not turn the discarded draft into a write.
      skipNextBlurCommit.current = true;
      onTitleCancel?.();
      event.currentTarget.blur();
    }
  };

  const handleBlur = () => {
    if (skipNextBlurCommit.current) {
      skipNextBlurCommit.current = false;
      return;
    }
    onTitleCommit?.();
  };

  return (
    <div className={cn("pm-task-details-header px-4 py-4 border-b shrink-0 sm:px-8", theme.borderPrimary)}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <input
            id="task-details-title-input"
            type="text"
            value={title || ''}
            onChange={editable ? (e) => onTitleChange(e.target.value) : undefined}
            onBlur={editable ? handleBlur : undefined}
            onKeyDown={editable ? handleKeyDown : undefined}
            aria-label={t('Task title')}
            placeholder={t('Enter title...')}
            readOnly={!editable}
            className={cn(
              "w-full bg-transparent text-xl font-semibold outline-none placeholder-gray-500 transition-all duration-200",
              theme.textPrimary,
              !editable && "cursor-default"
            )}
          />
        </div>
        <div className="flex items-center gap-2">
          {editable && canUndo && (
            <button 
              // Keep the title draft from committing on blur before Undo runs.
              onMouseDown={undoTitle ? (event) => event.preventDefault() : undefined}
              onClick={onUndo} 
              id="task-details-undo-button" 
              className={cn('p-2 rounded-lg transition-all duration-200', theme.hover)} 
              title={undoTitle || t('Undo Changes (Cmd+Z)')}
            >
              <RotateCcw size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
 * PANEL FOOTER COMPONENT
 * Footer with save button for new tasks
 * ============================================================================ */

export const PanelFooter: React.FC<PanelFooterProps> = ({
  onSave,
  disabled = false,
  disabledReason,
  theme
}) => {
  const { t } = useSettings();
  return (
    <div className={cn("pm-task-details-footer px-4 py-4 border-t shrink-0 sm:px-8", theme.borderPrimary)}>
      <div className="flex justify-end">
        <button 
          onClick={onSave}
          disabled={disabled}
          title={disabled ? disabledReason : undefined}
          className={cn(
            "px-6 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2",
            disabled
              ? "cursor-not-allowed opacity-50"
              : cn(theme.button, theme.hover)
          )}
        >
          <Save size={18} />
          {t('Save Task')}
        </button>
      </div>
    </div>
  );
};

/* ============================================================================
 * PANEL CONTENT WRAPPER
 * Wrapper for the main content area
 * ============================================================================ */

export const PanelContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="pm-task-details-content flex-1 min-h-0 overflow-y-auto px-4 py-5 transition-all duration-[200ms] ease-in-out sm:px-8 sm:py-6">
      <div className="space-y-6 transition-all duration-[200ms] ease-in-out">
        {children}
      </div>
    </div>
  );
};
